import React from 'react';
import { StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { MB_ButtonProps } from '@/components/general/button';
import { COLORS } from './constants';
import { MB_Modal } from './MB_Modal';
import { MB_ModalAnimationProps, MB_ModalChildren } from './typesAndInterfaces';

interface MB_ModalMobileBottomProps extends MB_ModalAnimationProps {
  /**
   * Modal visibility.
   */
  isVisible: boolean;

  /**
   * The modal's children.
   */
  children?: MB_ModalChildren;

  /**
   * Style for the view that wraps the modal's children.
   */
  childrenWrapperStyle?: StyleProp<ViewStyle>;

  /**
   * Callback called when the modal is dismissed. This should be the default way of listening for modal
   * dismiss since it is called only when modal hide animation is finished.
   */
  onDismiss?: (isVisible: false) => void;

  /**
   * Button props for the close button.
   */
  closeButtonProps?: MB_ButtonProps;

  /**
   * Title that is shown.
   */
  title?: string;

  /**
   * When set to true, hides the modal's content until the animations complete. This improves performance.
   */
  hideModalContentWhileAnimating?: boolean;

  /**
   * Presents the children without a scrollview.
   */
  withoutScrollView?: boolean;

  /**
   * You can pass a custom close button component instead of using the default one. Note that you will need to
   * handle the modal close yourself if you do this (most likely by calling `modalRef.hideModal()` to allow the
   * animation to play out).
   */
  closeButton?: React.JSX.Element;

  /**
   * Controls the color and size for the close button's X symbol.
   */
  closeButtonXProps?: { size?: number; color?: string };

  keyboardAwareProps?: {
    /**
     * Adds an extra offset to the keyboard. Useful if you want to stick elements
     * above the keyboard.
     */
    extraScrollHeight?: number;
  };

  /**
   * Determines when the keyboard should stay visible after a tap.
   *
   *  - 'never' (default), tapping outside of the focused text input when the keyboard
   * is up dismisses the keyboard. When this happens, children won't receive the tap.
   *  - 'always', the keyboard will not dismiss automatically, and the scroll view will
   * not catch taps, but children of the scroll view can catch taps.
   *  - 'handled', the keyboard will not dismiss automatically when the tap was handled
   * by a children, (or captured by an ancestor).
   *  - false, deprecated, use 'never' instead.
   *  - true, deprecated, use 'always' instead.
   */
  keyboardShouldPersistTaps?: boolean | 'always' | 'never' | 'handled' | undefined;
  titleCustomComponent?: React.JSX.Element;
}

const MB_ModalMobileBottom = (props: MB_ModalMobileBottomProps) => {
  return (
    <MB_Modal
      isVisible={props.isVisible}
      onDismiss={props.onDismiss}
      childrenWrapperStyle={[styles.childrenWrapper, props.childrenWrapperStyle]}
      scrollViewContentContainerStyle={styles.containerStyle}
      withoutScrollViewContainer={styles.containerStyle}
      closeOnBackdropPress
      title={props.title}
      titleCustomComponent={props.titleCustomComponent}
      withoutScrollView={props.withoutScrollView}
      hideModalContentWhileAnimating={props.hideModalContentWhileAnimating}
      animationIn={props.animationIn}
      animationInTiming={props.animationInTiming}
      animationOut={props.animationOut}
      animationOutTiming={props.animationOutTiming}
      closeButtonProps={props.closeButtonProps}
      closeButton={props.closeButton}
      closeButtonXProps={props.closeButtonXProps}
      keyboardAwareProps={props.keyboardAwareProps}
      keyboardShouldPersistTaps={props.keyboardShouldPersistTaps}
    >
      {props.children}
    </MB_Modal>
  );
};

export { MB_ModalMobileBottom };
export type { MB_ModalMobileBottomProps };

const styles = StyleSheet.create({
  childrenWrapper: {
    backgroundColor: COLORS.darkBackground,
    borderRadius: 0,
    marginBottom: 0,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    width: '100%',
    alignItems: 'center',
  },
  containerStyle: {
    justifyContent: 'flex-end',
  },
});
